import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import {
  InsuranceDetailsProps,
  InsuranceDetailsResponse,
  InsuranceFetchDetails,
  InsuranceState,
  InsuranceUploadProps,
  viewInsuranceDetailsResponse,
} from '../../../types/Finance/Insurance/insuranceTypes'
import { insuranceApi } from '../../../middleware/api/Finance/Insurance/insuranceApi'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'
import { currentYear } from '../../../utils/helper'

export const initialInsuranceState: InsuranceState = {
  isLoadingGet: ApiLoadingState.idle,
  isLoadingView: ApiLoadingState.idle,
  isLoadingUpload: false,
  isLoadingDownload: ApiLoadingState.idle,
  insuranceDetails: {
    size: 0,
    list: [] as InsuranceFetchDetails[],
  },
  year: currentYear.toString(),
  search: '',
  checkedId: [],
  paginationProps: {
    currentPage: 1,
    pageSize: 20,
  },
  isDelete: false,
  viewInsuranceDetails: {
    id: 0,
    discription: '',
    discription2: null,
  },
}

const uploadFolderInsurance = createAsyncThunk(
  'insurance/uploadFolderInsurance',
  async (props: InsuranceUploadProps, thunkApi) => {
    try {
      return await insuranceApi.uploadFolder(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const saveInsurance = createAsyncThunk(
  'insurance/saveInsurance',
  async (
    {
      empId,
      year,
    }: {
      empId: number
      year: string
    },
    thunkApi,
  ) => {
    try {
      return await insuranceApi.saveInsurance({
        empId,
        year,
      })
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getInsuranceDetails = createAsyncThunk(
  'insurance/getInsuranceDetails',
  async (props: InsuranceDetailsProps, thunkApi) => {
    try {
      return await insuranceApi.getInsuranceDetails(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const deleteMultipleInsuranceDetails = createAsyncThunk(
  'insurance/deleteMultipleInsuranceDetails',
  async (props: number[], thunkApi) => {
    try {
      return await insuranceApi.deleteMultipleInsuranceDetails(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const downloadInsurance = createAsyncThunk(
  'insurance/downnloadInsurance',
  async (
    {
      empId,
      year,
    }: {
      empId: number
      year: string
    },
    thunkApi,
  ) => {
    try {
      return await insuranceApi.downloadInsurance({ empId, year })
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const insuranceDetail = createAsyncThunk(
  'insurance/viewInsurance',
  async (_, thunkApi) => {
    try {
      return await insuranceApi.viewInsuranceDetails()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const internSlice = createSlice({
  name: 'Insurance',
  initialState: initialInsuranceState,
  reducers: {
    setYear: (state, action) => {
      state.year = action.payload
    },
    setSearch: (state, action) => {
      state.search = action.payload
    },
    setIsLoadingUpload: (state, action) => {
      state.isLoadingUpload = action.payload
    },
    setCheckedId: (state, action) => {
      state.checkedId = action.payload
    },
    setPaginationProps: (state, action) => {
      state.paginationProps = action.payload
    },
    setIsDelete: (state, action) => {
      state.isDelete = action.payload
    },
  },
  extraReducers(builder) {
    builder.addCase(getInsuranceDetails.pending, (state) => {
      state.isLoadingGet = ApiLoadingState.loading
    })
    builder.addCase(getInsuranceDetails.fulfilled, (state, action) => {
      state.isLoadingGet = ApiLoadingState.succeeded
      state.insuranceDetails = action.payload
    })
    builder.addCase(insuranceDetail.pending, (state) => {
      state.isLoadingView = ApiLoadingState.loading
    })
    builder.addCase(insuranceDetail.fulfilled, (state, action) => {
      state.isLoadingView = ApiLoadingState.succeeded
      state.viewInsuranceDetails = action.payload
    })
    builder.addCase(insuranceDetail.rejected, (state) => {
      state.isLoadingView = ApiLoadingState.failed
    })
  },
})
const isLoadingGet = (state: RootState): LoadingState =>
  state.insurance.isLoadingGet

const isLoadingDownload = (state: RootState): LoadingState =>
  state.insurance.isLoadingDownload

const isLoadingUpload = (state: RootState): boolean =>
  state.insurance.isLoadingUpload

const getInsuranceDetailsSelector = (
  state: RootState,
): InsuranceFetchDetails[] => state.insurance.insuranceDetails.list

const getInsuranceDetailsSize = (state: RootState): number =>
  state.insurance.insuranceDetails.size

const getAllInsuranceDetails = (state: RootState): InsuranceDetailsResponse =>
  state.insurance.insuranceDetails

const getYear = (state: RootState): string => state.insurance.year
const getSearch = (state: RootState): string => state.insurance.search
const getCheckId = (state: RootState): number[] => state.insurance.checkedId
const getPaginationProps = (
  state: RootState,
): {
  currentPage: number
  pageSize: number
} => state.insurance.paginationProps
const getIsDelete = (state: RootState): boolean => state.insurance.isDelete
const getIsLoadingView = (state: RootState): ApiLoadingState =>
  state.insurance.isLoadingView
const getgetInsuranceDetailsInsurance = (
  state: RootState,
): viewInsuranceDetailsResponse => state.insurance.viewInsuranceDetails

const insuranceThunk = {
  uploadFolderInsurance,
  getInsuranceDetails,
  downloadInsurance,
  saveInsurance,
  deleteMultipleInsuranceDetails,
  insuranceDetail,
}

const insuranceSelectors = {
  isLoadingGet,
  getInsuranceDetailsSelector,
  getInsuranceDetailsSize,
  getYear,
  getSearch,
  getAllInsuranceDetails,
  isLoadingDownload,
  isLoadingUpload,
  getCheckId,
  getPaginationProps,
  getIsDelete,
  getgetInsuranceDetailsInsurance,
  getIsLoadingView,
}
export const insuranceService = {
  ...insuranceThunk,
  actions: internSlice.actions,
  selectors: insuranceSelectors,
}
export default internSlice.reducer
